import CancelBackButton from "Common/Components/Buttons/CancelBackButton";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import FormHeader from "components/FormAndDetailHeader/FormAndDetailHeader";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import React, { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchProductById } from "slices/Masters/product/thunk";
import DOMPurify from "dompurify";
import { config } from "config/env";
import Chip from "components/Chips/Chip";
import CancelButton from "Common/Components/Buttons/CancelButton";

const ProductView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    dispatch(fetchProductById(id))
      .unwrap()
      .then((res: any) => {
        const data = res?.data ?? res;
        setProduct(data);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id, dispatch]);

  const imageUrl = (() => {
    const raw = product?.image_url ?? product?.attachment_url ?? null;
    if (!raw) return null;
    if (raw.startsWith("/"))
      return `${(config.api?.baseUrl || "").replace(/\/$/, "")}${raw}`;
    return raw;
  })();
  const fileName = decodeURIComponent(imageUrl?.split("/").pop() || "file") 
  const descriptionHtml = product?.prod_description
    ? DOMPurify.sanitize(product.prod_description)
    : "";

  return (
    <PageContainer>
      <FormHeader
        title="Product Details"
        firstLabel="Product"
        secondLabel="Product Details"
        handleArrowClick={() => navigate("/master/product")}
      />
      <hr />

      {loading ? (
        <LoadingSpinner />
      ) : !product ? (
        <Row className="justify-content-center">
          <Col md={6}>
            <div className="text-center py-5">
              <h4>Product not found</h4>
              <Link to="/master/product" className="btn btn-primary">
                Back to Products
              </Link>
            </div>
          </Col>
        </Row>
      ) : (
        <>
          <style>{`
            .product-view-description img {
              max-width: 100%;
              height: auto;
              max-height: 280px;
              object-fit: contain;
            }
          `}</style>
          <Card>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <p>
                    <strong>Product Name:</strong>{" "}
                    {product.product_name ?? "-"}
                  </p>
         
                  <p>
                    <strong>Sub-Category:</strong>{" "}
                    {product.sub_category?.sub_category_name ?? "-"}
                  </p>
              
                  <p>
                    <strong>Make:</strong>{" "}
                    {product.make?.make_name ?? "-"}
                  </p>
                  <p>
                    <strong>SKU Code:</strong>{" "}
                    {product.product_sku ?? "-"}
                  </p>
                  <p>
                    <strong>Unit Price:</strong>{" "}
                    {product.unit_price != null && product.unit_price !== ""
                      ? `₹${formatIndianAmount(product.unit_price)}`
                      : "₹0.00"}
                  </p>
      
                  <p>
                    <strong>GST %:</strong>{" "}
                    {product.hsn?.gst?.gst_percentage ?? "-"}
                  </p>
                </Col>
                <Col md={6}>
                <p>
                    <strong>Category:</strong>{" "}
                    {product.category?.category_name ?? "-"}
                  </p>
                  {product.product_types?.length > 0 && (
                    <p >
                      <strong>Types:</strong>{" "}
                      <span className="d-inline-flex flex-wrap gap-1">
                        {product.product_types.map((pt: any, idx: number) => (
                          <Chip key={pt?.type?.type_id ?? idx}>
                            {pt?.type?.type_name ?? "-"}
                          </Chip>
                        ))}
                      </span>
                    </p>
                  )}
                  <p>
                    <strong>Model:</strong>{" "}
                    {product.model?.model_name ?? "-"}
                  </p>
          
                  <p>
                    <strong>Unit :</strong>{" "}
                    {product.unit?.unit_name ?? "-"}
                  </p>
           
          
                  <p>
                    <strong>HSN Code:</strong>{" "}
                    {product.hsn?.hsn_code ?? "-"}
                  </p>
                </Col>
              </Row>

              {imageUrl &&  (
                <>
                  <hr />
                  <Row className="mb-3">
                    <Col md={12}>
                      <p className="fw-semibold mb-2">Product Image</p>
                      <div className="d-flex gap-3 align-items-center">
             

                  <a
                    href={imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-underline"
                    title={fileName || `Attachment`} // 👈 hover shows full name
                  >
                    <span
                      className="badge bg-secondary text-truncate"
                      style={{
                        fontSize: "0.75rem",
                        maxWidth: "200px", // 👈 controls ~20–30 chars
                        display: "inline-block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                      }}
                    >
                      {fileName || `Attachment `}
                    </span>
                  </a>
                </div>
                    </Col>
                  </Row>
                </>
              )}

              {descriptionHtml && (
                <>
                  <hr />
                  <Row className="mb-3">
                    <Col md={12}>
                      <p className="fw-semibold mb-2">Description</p>
                      <div
                        className="product-view-description border rounded p-3 bg-light"
                        style={{
                          minHeight: "60px",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                        dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                      />
                    </Col>
                  </Row>
                </>
              )}

              {(product.remark ?? "").trim() !== "" && (
                <>
                  <hr />
                  <Row className="mb-3">
                    <Col md={12}>
                      <p>
                        <strong>Remark:</strong>{" "}
                        {product.remark}
                      </p>
                    </Col>
                  </Row>
                </>
              )}
    <hr />

<div className="d-flex flex-wrap justify-content-center gap-3 mt-5">
  <CancelButton showConfirmation={false} redirectTo={"/master/product"} />
</div>
       
            </Card.Body>
          </Card>
        </>
      )}
    </PageContainer>
  );
};

export default ProductView;
